import { FC, useMemo } from 'react';

import { GiftDeliveryForRebatePending } from '@shared/api/giftDelivery/giftDelivery.interface';
import { useSearchParams } from 'react-router-dom';

import { useTitleHook } from '../../../bugo/hooks/useTitleHook';
import { UnifiedMonthlySection } from '../../components/agencyGiftSettlement/UnifiedMonthlySection';
import { useAgencyGiftSettlementData } from '../../hooks/useAgencyGiftSettlementData';
import { useAgencyUnifiedSettlementData } from '../../hooks/useAgencyUnifiedSettlementData';
import { computeRebateRow, computeRebateTotals } from '../../utils/rebate-table.utils';
import { AgencyGiftCouponSettlementView } from '../AgencyGiftCouponSettlement/AgencyGiftCouponSettlementView';
import { AgencyGiftSettlementView } from '../AgencyGiftSettlement/AgencyGiftSettlementView';

// ─────────────────────────────────────────────
// AgencyGiftSettlementUnifiedContainer
//
// 상조관리자 답례품 정산을 한 페이지로 합친다 (2026-07-31 요청).
// 백엔드 정산 문서는 배송형·쿠폰형으로 계속 분리돼 있고, 여기서는
// - '월별 합계' 탭: 두 정산을 정산월로 묶어 총금액만 합산해 보여주고
// - '배송형'·'쿠폰형' 탭: 기존 화면을 그대로 마운트해 건별 상세를 본다.
// 탭은 ?tab= 쿼리로 유지해 사이드바에서 바로 진입할 수 있다.
// ─────────────────────────────────────────────

type UnifiedTab = 'summary' | 'delivery' | 'coupon';

const TABS: { key: UnifiedTab; label: string }[] = [
  { key: 'summary', label: '월별 합계' },
  { key: 'delivery', label: '배송형' },
  { key: 'coupon', label: '쿠폰형' },
];

const isUnifiedTab = (v: string | null): v is UnifiedTab =>
  v === 'summary' || v === 'delivery' || v === 'coupon';

export const AgencyGiftSettlementUnifiedContainer: FC = () => {
  useTitleHook('답례품 정산');

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: UnifiedTab = isUnifiedTab(tabParam) ? tabParam : 'summary';

  const setTab = (next: UnifiedTab) => {
    // 탭은 히스토리를 더럽히지 않게 replace (뒤로가기는 정산 페이지 밖으로)
    setSearchParams(next === 'summary' ? {} : { tab: next }, { replace: true });
  };

  // 배송형 데이터는 한 번만 받아 '월별 합계'와 '배송형' 탭이 공유한다
  const rebate = useAgencyGiftSettlementData();
  const { couponItems, couponLoading, couponPendingNet, couponPendingLoading } =
    useAgencyUnifiedSettlementData();

  // 미정산(아직 정산서로 안 묶인) 배송완료분 예상 수익금 — 미정산 배송 내역 탭 합계와 동일 계산
  const pendingDeliveryAmount = useMemo(() => {
    if (!rebate.deliveredEntries) return undefined;
    const rows = rebate.deliveredEntries.map((d) =>
      computeRebateRow({
        delivery: d as unknown as GiftDeliveryForRebatePending,
        quantity: d.quantity,
        hasSettlementInfo: true,
      }),
    );
    return computeRebateTotals(rows).totalAmount;
  }, [rebate.deliveredEntries]);

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">답례품 정산</h1>

      {/* 탭 */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-0">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`w-36 border-b-2 px-5 py-2.5 text-center text-sm font-medium transition-colors ${
                tab === t.key
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'summary' && (
        <div className="rounded-lg bg-white p-6 shadow">
          <UnifiedMonthlySection
            rebateItems={rebate.historyItems}
            couponItems={couponItems}
            loading={rebate.historyLoading || couponLoading}
            pendingDeliveryAmount={pendingDeliveryAmount}
            pendingCouponAmount={couponPendingNet}
            pendingLoading={rebate.deliveredLoading || couponPendingLoading}
            onOpenDelivery={() => setTab('delivery')}
            onOpenCoupon={() => setTab('coupon')}
          />
        </div>
      )}

      {tab === 'delivery' && <AgencyGiftSettlementView {...rebate} />}

      {tab === 'coupon' && <AgencyGiftCouponSettlementView />}
    </div>
  );
};
